import React, { useState } from 'react';

import { Button, Modal } from '@common/index';
import { Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { flowActions } from '@store/slices';
import { AppDispatch, State, tabActions } from '@store/index';
import { ArticleOutlined } from '@mui/icons-material';
import { useTheme } from '../../styles/theme';
import { BasicInput } from '@common/Expression/Basic';
import { config } from '../Flow/Nodes/index';
import { getFullEdges } from '@common/utils/flow';

export const convertVariableName = (input: string) => {
  const formatted = input.replace(/[^a-zA-Z0-9]/g, '_');
  return formatted.toLowerCase().trim();
};

export const AddDataPrefix: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();

  const [prefix, setPrefix] = useState<string>('');

  const dataPrefixNodeId = useSelector((state: State) => state.flow.showDataPrefix);
  const dataPrefixes = useSelector((state: State) => state.tabs.active.flow?.data_prefixes) || {};
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const edges = useSelector((state: State) => state.tabs.active.edges);
  const full_edges = getFullEdges(edges, commands);

  const open = Boolean(dataPrefixNodeId);
  const setOpen = (val: string | null) => dispatch(flowActions.setShowDataPrefix(val));

  const getConfigNodePrefixes = () => {
    const prefixes = [];

    for (const nodeConfig of Object.values(config)) {
      const command_id = nodeConfig?.config?.command_id;
      if (command_id && command_id.includes('.')) {
        const [prefixName] = command_id.split('.');
        prefixes.push(prefixName);
      }
    }

    return prefixes;
  };

  const getExistingPrefixes = () => {
    const prefixes = [];

    for (const existingPrefix of Object.values(dataPrefixes)) {
      if (existingPrefix) prefixes.push(existingPrefix);
    }

    return prefixes;
  };

  const existingPrefixes = getExistingPrefixes();
  const duplicatePrefix = existingPrefixes.length > 0 && existingPrefixes.includes(prefix);
  const restrictedPrefix = getConfigNodePrefixes().includes(prefix);
  const isAddDisabled = !prefix || prefix.length > 64 || restrictedPrefix || duplicatePrefix;

  const addPrefix = () => {
    if (!open) return;

    if (dataPrefixNodeId) {
      dispatch(tabActions.addDataPrefix({ node_id: dataPrefixNodeId, prefix }));
      dispatch(tabActions.setSaved(false));

      const dataEdges = full_edges.filter(
        (full) => full.edge.source === dataPrefixNodeId && full.source_anchor.variant === 'data'
      );

      for (const full of dataEdges) {
        dispatch(tabActions.removeEdgeById({ edgeId: full.edge.id }));
      }
    }

    closeModal();
  };

  const getError = () => {
    if (prefix.length > 64) return 'The prefix name has too many characters. The max characters is 64.';
    if (restrictedPrefix) return 'This is a restricted prefix name. Please choose another name.';
    if (duplicatePrefix) return 'This prefix name is already being used by another node in this workflow.';
    return undefined;
  };

  const closeModal = () => {
    setOpen(null);
    setPrefix('');
  };

  return (
    <Modal
      open={Boolean(open)}
      setOpen={closeModal as (val: boolean | string | null) => void}
      header='Data Prefix'
      background={theme.primary}
      icon={<ArticleOutlined />}
      info={`Adding a data prefix to this node will make the data available in all nodes in this workflow. This is particularly useful for nodes that pass data to many nodes. Enabling this will prevent you from connecting data edges from this node. It will also remove any existing data edges.\n\nThe name you provide below will be used to prefix the data of this node. For example, if the prefix is named "trigger" for the Request node, data is accessed as "trigger.http.request...".`}
      body={
        <Stack>
          <BasicInput
            key={`data-prefix-${dataPrefixNodeId}`}
            keyProp={`data-prefix-${dataPrefixNodeId}`}
            value={convertVariableName(prefix)}
            setValue={(newValue: string) => {
              setPrefix(convertVariableName(newValue));
            }}
            placeholder='Data Prefix'
            minRows={1}
            maxRows={1}
            error={getError()}
            wrapping={false}
          />
        </Stack>
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button small onClick={closeModal}>
            Cancel
          </Button>
          <Button small customColor='primary' onClick={addPrefix} disabled={isAddDisabled}>
            Add Prefix
          </Button>
        </Stack>
      }
    />
  );
};
